import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import { Empty, Loading, Modal } from '../../components/Ui';
import { fileUrl } from '../../api/client';
import { Reveal, stagger } from '../../components/Motion';
import { useApp } from '../../context/AppContext';
import { colors } from '../../theme';
import { timeAgo } from '../../utils/format';

const ICONS = {
  order: '🧾', booking: '📅', job: '💼', vehicle: '🚗',
  ride: '🚕', payment: '💳', general: '🔔',
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { refreshUnread } = useApp();
  const [items, setItems] = useState([]);
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.notifications.list({ limit: 50 })
      .then((res) => setItems(res.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = (n) => {
    if (!n.isRead) {
      api.notifications.markRead(n._id).then(refreshUnread).catch(() => {});
      setItems((list) => list.map((i) => (i._id === n._id ? { ...i, isRead: true } : i)));
    }
    const data = n.data || {};
    if (data.orderId) navigate(`/orders/${data.orderId}`);
    else if (data.bookingId) navigate(`/bookings/${data.bookingId}`);
    else if (data.jobId) navigate(`/jobs/${data.jobId}`);
    else if (data.vehicleId) navigate(`/vehicles/${data.vehicleId}`);
    else if (data.rideId) navigate(`/ride/${data.rideId}`);
    // A greeting with a photo/video attached opens straight onto it.
    else if (data.video || data.image) setMedia({ title: n.title, body: n.body, image: data.image, video: data.video });
    else if (data.wishId) navigate('/wishes');
  };

  const markAll = async () => {
    await api.notifications.markAllRead();
    setItems((list) => list.map((i) => ({ ...i, isRead: true })));
    refreshUnread();
  };

  const unread = items.filter((i) => !i.isRead).length;

  return (
    <div style={{ maxWidth: 720 }}>
      <PageHeader
        title="Notifications"
        subtitle={`${unread} unread`}
        right={items.length ? (
          <button type="button" className="link-btn" onClick={markAll}>Mark all read</button>
        ) : null}
      />

      {loading ? <Loading /> : !items.length ? (
        <Empty
          emoji="🔔"
          title="No notifications yet"
          text="Order updates, booking confirmations and offers land here."
        />
      ) : (
        items.map((item, i) => (
          <Reveal key={item._id} delay={stagger(i, 35)}>
            <button
              type="button"
              onClick={() => open(item)}
              className="press"
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%', textAlign: 'left',
                background: item.isRead ? '#fff' : colors.primaryLight,
                border: `1px solid ${item.isRead ? colors.line : colors.primarySoft}`,
                borderRadius: 14, padding: 14, marginBottom: 10,
              }}
            >
              <span
                style={{
                  width: 40, height: 40, borderRadius: 12, flex: '0 0 auto',
                  background: item.isRead ? colors.bg : '#fff',
                  display: 'grid', placeItems: 'center', fontSize: 17,
                }}
              >
                {ICONS[item.type] || '🔔'}
              </span>
              <span className="grow">
                <span className="t-title" style={{ display: 'block' }}>{item.title}</span>
                {item.body ? (
                  <span className="t-small" style={{ display: 'block', marginTop: 3, lineHeight: 1.55 }}>
                    {item.body}
                  </span>
                ) : null}
                <span className="t-tiny" style={{ display: 'block', marginTop: 5 }}>
                  {timeAgo(item.createdAt)}
                </span>
              </span>
              {!item.isRead ? (
                <span
                  style={{
                    width: 8, height: 8, borderRadius: 4, background: colors.accent,
                    flex: '0 0 auto', marginTop: 6,
                  }}
                />
              ) : null}
            </button>
          </Reveal>
        ))
      )}
      <Modal open={!!media} title={media ? media.title : ''} onClose={() => setMedia(null)} wide>
        {media && media.video ? (
          <video src={fileUrl(media.video)} controls autoPlay style={{ width: '100%', maxHeight: 460, borderRadius: 12, background: '#000' }} />
        ) : media && media.image ? (
          <img src={fileUrl(media.image)} alt="" style={{ width: '100%', maxHeight: 460, objectFit: 'contain', borderRadius: 12 }} />
        ) : null}
        {media && media.body ? <div className="t-body" style={{ marginTop: 10 }}>{media.body}</div> : null}
      </Modal>
    </div>
  );
};

export default NotificationsPage;
